/**
 * The time machine's controls: a small pill, bottom left, for staff only.
 * Flipping a control rewrites the URL (so the view can be sent to the team)
 * and re-renders in place.
 */
import { useState } from 'react';
import { Clock3, X } from 'lucide-react';
import { STAGES } from '@/lib/stages';
import type { Preview } from './preview';
import { EXPERIENCE_KEYS } from '../registry';

export function PreviewPanel({
  preview,
  set,
  activeKey,
}: {
  preview: Preview;
  set: (n: Partial<Omit<Preview, 'allowed'>>) => void;
  activeKey: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (!preview.allowed) return null;
  const on = preview.stage || preview.at || preview.viewer !== 'real' || preview.xp;
  // Out of sight on the real site: the page already follows the real date.
  // It shows on localhost, while a preview is on, or when asked for (?tm).
  const summoned =
    /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) ||
    new URLSearchParams(window.location.search).has('tm');
  if (!on && !summoned) return null;
  const field =
    'w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-[#1c2b39]';
  return (
    <div className="fixed bottom-4 left-4 z-[2147483001] font-sans text-[#1c2b39]">
      {open ? (
        <div className="w-72 rounded-2xl bg-white/95 p-4 shadow-2xl ring-1 ring-black/10 backdrop-blur">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-bold tracking-widest uppercase">Time machine</p>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <label className="mb-2 block text-xs font-semibold">
            Stage
            <select
              className={field}
              value={preview.stage ?? ''}
              onChange={(e) =>
                set({ stage: (e.target.value || null) as Preview['stage'], at: null })
              }
            >
              <option value="">Real (today)</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="mb-2 block text-xs font-semibold">
            Exact moment
            <input
              type="datetime-local"
              className={field}
              value={preview.at?.slice(0, 16) ?? ''}
              onChange={(e) =>
                set({ at: e.target.value ? `${e.target.value}:00` : null, stage: null })
              }
            />
          </label>
          <label className="mb-2 block text-xs font-semibold">
            Viewing as
            <select
              className={field}
              value={preview.viewer}
              onChange={(e) => set({ viewer: e.target.value as Preview['viewer'] })}
            >
              <option value="real">Me</option>
              <option value="guest">Signed out</option>
              <option value="attendee">A registered attendee (demo)</option>
            </select>
          </label>
          <label className="mb-3 block text-xs font-semibold">
            Experience
            <select
              className={field}
              value={preview.xp ?? ''}
              onChange={(e) => set({ xp: e.target.value || null })}
            >
              <option value="">The event&rsquo;s own{activeKey ? ` (${activeKey})` : ''}</option>
              {EXPERIENCE_KEYS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="w-full rounded-full bg-gray-100 py-2 text-sm font-semibold"
            onClick={() => set({ stage: null, at: null, viewer: 'real', xp: null })}
          >
            Back to now
          </button>
          <p className="mt-2 text-[11px] text-gray-500">
            Only staff see this. Changes are not saved while you are pretending.
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold shadow-lg ring-1 ring-black/10 ${on ? 'bg-amber-300' : 'bg-white/90'}`}
        >
          <Clock3 className="h-4 w-4" />{' '}
          {on
            ? [
                preview.stage ?? preview.at?.slice(0, 16),
                preview.viewer !== 'real' ? preview.viewer : null,
                preview.xp,
              ]
                .filter(Boolean)
                .join(' · ')
            : 'Time machine'}
        </button>
      )}
    </div>
  );
}
